"use client";

import { useEffect, useRef } from "react";

// How often to compare the date while the page stays open. The check itself is
// free (no network) — it only triggers onChange when the calendar day rolls over.
const CHECK_INTERVAL_MS = 60_000;

function getLocalDateKey(): string {
  return new Date().toDateString();
}

/**
 * Calls `onChange` once whenever the user's local calendar date changes while
 * the page is open (e.g. checkout left open overnight, laptop slept past midnight).
 *
 * We poll the date instead of scheduling a single timeout for midnight because
 * browsers pause/throttle timers during sleep and in background tabs, so a
 * midnight timeout can fire late or not at all. Focus/visibility/pageshow
 * checks catch the rollover immediately on wake or on bfcache restore.
 */
export function useLocalDateChange(onChange: () => void) {
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let dateKey = getLocalDateKey();

    const check = () => {
      const current = getLocalDateKey();
      if (current === dateKey) return;
      dateKey = current;
      onChangeRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };

    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
    };
  }, []);
}
